/**
 * BulkEditAction — mounts the already-built `BulkEditDrawer` for the
 * media-units catalog list, controlled by the caller.
 *
 * The "Edytuj masowo" trigger lives in the shared floating `BulkActionToolbar`
 * (rendered by the route); this component owns only the drawer itself, driven
 * by the `isOpen`/`onOpenChange` props the toolbar action wires up. This keeps
 * exactly one selection affordance and one floating toolbar on the catalog
 * list instead of a separate below-table trigger button.
 *
 * This component stays permanently mounted in the route tree regardless of
 * `isOpen` (only its rendered output is gated), so the route remounts it via a
 * changing `key` on every open to guarantee a fresh `serverError` — an
 * effect-based reset would flash the previous session's stale error for one
 * paint before clearing it.
 *
 * Owns the `useBulkEditMutation` call and the updated/failed result toast. On
 * request failure the drawer stays open with an inline server error, matching
 * the drawer's own "keep input intact" contract.
 *
 * Traceability: bulk-edit-media / Requirement: Bulk-edit drawer flow with
 * confirm summary and result toast.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { toast } from 'sonner';
import { MediaTypeEnum } from '@ooh/contracts';
import type { BulkEditRequest, MediaUnitFilters } from '@ooh/contracts';
import { BulkEditDrawer } from './bulk-edit-drawer';
import type { BulkEditFieldValue } from './bulk-edit-drawer';
import { useBulkEditMutation } from '../api/mutations';
import { suppliersQuery, filterOptionsQuery } from '../api/queries';
import { buildMediaTypeOptions } from '../lib/filter-fields';
import { buildMediaTypeOptionLabels } from '../../../lib/media-type-options';

export interface BulkEditActionProps {
  /** Holder of media_units.bulk_edit — renders the drawer. */
  canBulkEdit: boolean;
  /** Controlled open state, driven by the shared BulkActionToolbar's "Edytuj masowo" action. */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Explicit checkbox selection (useMediaBulkSelection.selectedIds). */
  selectedIds: number[];
  /** Active "Select all N matching" filter, when the user activated that mode. */
  selectAllFilter: MediaUnitFilters | null;
  /** Total rows matching the current filter (for the confirm count + cap check). */
  totalMatching: number;
  /**
   * Of `selectedIds`, how many are not in the current results (id-based
   * selection only — a filter-based selection has no concept of "hidden").
   */
  hiddenCount?: number;
  /** Called after a successful bulk edit so the caller can clear its selection. */
  onSuccess?: () => void;
}

export function BulkEditAction({
  canBulkEdit,
  isOpen,
  onOpenChange,
  selectedIds,
  selectAllFilter,
  totalMatching,
  hiddenCount,
  onSuccess,
}: BulkEditActionProps) {
  const { t } = useTranslation('inventory');
  const { t: tMap } = useTranslation('map');
  const { auth } = useRouteContext({ from: '__root__' });
  const mutation = useBulkEditMutation();

  // Reuses the same suppliers source as the add/edit forms (suppliersQuery, shared via
  // src/lib/suppliers-query) and the same distinct-cities source already loaded for
  // the catalog filter panel (filterOptionsQuery) — no parallel data fetch. Both share a
  // query key with the page-level fetch, so TanStack Query serves cached data instead of
  // firing a second request.
  const { data: suppliersData } = useQuery(suppliersQuery(auth));
  const { data: filterOptionsData } = useQuery(filterOptionsQuery(auth));

  const supplierOptions = React.useMemo(
    () =>
      (Array.isArray(suppliersData) ? suppliersData : []).map((s) => ({
        value: String(s.id),
        label: s.name,
      })),
    [suppliersData],
  );
  // Full MediaTypeEnum, not just types already present in the catalog — a bulk edit
  // must be able to set any valid media type, including ones not yet used anywhere.
  const mediaTypeOptions = React.useMemo(
    () => buildMediaTypeOptions([...MediaTypeEnum.options], buildMediaTypeOptionLabels(tMap)),
    [tMap],
  );
  const cities = filterOptionsData?.cities ?? [];

  const [serverError, setServerError] = React.useState<string | null>(null);

  if (!canBulkEdit) return null;

  async function handleSubmit(payload: BulkEditFieldValue) {
    setServerError(null);
    try {
      const response = await mutation.mutateAsync({
        ...payload,
        selection: selectAllFilter != null ? { filter: selectAllFilter } : { ids: selectedIds },
        // I/O boundary: the drawer's per-field value is a plain string|number union;
        // the mutation's discriminated-union request type narrows on `field`, which
        // TS cannot infer from a spread of that looser shape.
      } as BulkEditRequest);
      toast.success(
        t('bulkEdit.successToast', { updated: response.updated, failed: response.failed }),
      );
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Show a friendly translated message — the raw backend exception text
      // (e.g. "Bad Request Exception") is never suitable for end users.
      setServerError(t('bulkEdit.errors.generic'));
    }
  }

  return (
    <BulkEditDrawer
      isOpen={isOpen}
      selectedCount={selectedIds.length}
      selectAllFilter={selectAllFilter}
      totalMatching={totalMatching}
      hiddenCount={hiddenCount}
      onSubmit={handleSubmit}
      onCancel={() => onOpenChange(false)}
      t={t}
      isPending={mutation.isPending}
      serverError={serverError}
      mediaTypeOptions={mediaTypeOptions}
      supplierOptions={supplierOptions}
      cities={cities}
    />
  );
}
